import { toast } from "sonner";
import i18n from "@/i18n";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const LAST_BUILD_STORAGE_KEY = "sida-last-build-id";
const CHUNK_ERROR_RELOAD_KEY = "sida-reloaded-on-chunk-error";

interface VersionInfo {
  build: string;
}

async function fetchServerBuildId(): Promise<string | null> {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const json = (await res.json()) as VersionInfo;
    return json?.build ?? null;
  } catch {
    return null;
  }
}

function showUpdateToast() {
  const message = i18n.t("versionUpdate.newVersionAvailable");
  const actionLabel = i18n.t("versionUpdate.reload");
  toast(message, {
    duration: Infinity,
    action: {
      label: actionLabel,
      onClick: () => window.location.reload(),
    },
  });
}

export async function checkForAppUpdate(manual = false) {
  // In local development builds stay stable; skip polling to avoid noise.
  if (import.meta.env.DEV && !manual) return;

  const serverBuildId = await fetchServerBuildId();
  if (!serverBuildId) return;

  const lastBuildId = localStorage.getItem(LAST_BUILD_STORAGE_KEY);
  if (lastBuildId && lastBuildId !== serverBuildId) {
    showUpdateToast();
  }
  localStorage.setItem(LAST_BUILD_STORAGE_KEY, serverBuildId);
}

export function startAppVersionWatcher() {
  if (import.meta.env.DEV) return;

  checkForAppUpdate();
  const intervalId = setInterval(() => checkForAppUpdate(), CHECK_INTERVAL_MS);

  const handleVisibility = () => {
    if (document.visibilityState === "visible") checkForAppUpdate();
  };
  document.addEventListener("visibilitychange", handleVisibility);

  // Cleanup helper for tests / HMR.
  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
}

export function isChunkLoadError(message: unknown): boolean {
  if (typeof message !== "string") return false;
  return (
    message.includes("ChunkLoadError") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Loading chunk") ||
    message.includes("Unable to preload CSS")
  );
}

/**
 * One reload per browser tab for a stale-build chunk failure. Every caller must
 * go through this single guard: two guards with different keys would reload the
 * same failure twice and feel like a refresh loop.
 */
export function reloadOnceOnChunkError() {
  if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, "1");
  window.location.reload();
}

export function installChunkLoadErrorHandler() {
  window.addEventListener("error", (event) => {
    const message = event.error?.message ?? event.message;
    if (isChunkLoadError(message)) {
      reloadOnceOnChunkError();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
          ? reason.message
          : String(reason);
    if (isChunkLoadError(message)) {
      reloadOnceOnChunkError();
    }
  });
}
